/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { postOfflineSale } from "../../api/orderApi";
import Breadcrumbs from "../../components/customBreadcrumb/customBreadcrumb";
import CustomButton from "../../components/customButton/customButton";
import { buildStaffClubEventQuery } from "../../helpers/eventClubScope";
import {
  employeeMaySellOfflineEvent,
  normalizeTicketOfflineSoldOut,
} from "../../helpers/eventOfflineAssignment";
import useAuth from "../../hooks/useAuth";
import useMyEmpref from "../../hooks/useMyEmpref";
import Constant from "../../util/constant";
import { currentEventListResponce } from "../../store/features/event/eventSlice";
import { showToast } from "../../store/features/toast/toastSlice";
import {
  buyerDisplayPrice,
  buyerMaxQty,
  isPhasedPricingEnabled,
  resolvePhaseStatus,
} from "../../helpers/ticketSalePhases";
import "../eventPricingHub/eventPricingHub.css";

const PAY_METHODS = [
  { value: "CASH", label: "CASH" },
  { value: "UPI", label: "UPI" },
  { value: "POS", label: "POS" },
];

/** One sale may include up to this many tickets in a single API call. */
const OFFLINE_SALE_QTY_MAX = 10;
const OFFLINE_SALE_QTY_MIN = 1;

const DEFAULT_CURRENCY =
  process.env.REACT_APP_DEFAULT_CURRENCY || "INR";

function clampOfflineQty(value) {
  const parsed = parseInt(value, 10);
  if (!Number.isFinite(parsed)) return OFFLINE_SALE_QTY_MIN;
  return Math.min(
    OFFLINE_SALE_QTY_MAX,
    Math.max(OFFLINE_SALE_QTY_MIN, parsed)
  );
}

function formatSaleAmount(n, currency) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  if (String(currency).toUpperCase() === "INR") {
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  return `${num.toFixed(2)} ${currency}`;
}

function eventClubId(ev) {
  if (!ev || typeof ev !== "object") return null;
  const c = ev.clubid ?? ev.clubId;
  if (c != null && c !== "") return c;
  if (ev.club && typeof ev.club === "object") {
    return ev.club._id ?? ev.club.id ?? null;
  }
  return ev.club ?? null;
}

/**
 * POST /order/offlineSale — club staff sells tickets for the club’s events.
 */
const OfflineSaleContainer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { managedClubId, userId, isClubEmployee, isClubManager } = useAuth();
  const { empref: myEmpref, loading: myEmprefLoading } = useMyEmpref();
  const events = useSelector((s) => s?.event?.currentEventListResponce ?? []);

  const [eventId, setEventId] = useState("");
  const [ticketIndex, setTicketIndex] = useState(0);
  const [qty, setQty] = useState(String(OFFLINE_SALE_QTY_MIN));
  const [payment, setPayment] = useState("CASH");
  const [buyername, setBuyername] = useState("");
  const [buyerphone, setBuyerphone] = useState("");
  const [buyeremail, setBuyeremail] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState(null);
  const [pendingBody, setPendingBody] = useState(null);
  const [failOpen, setFailOpen] = useState(false);
  const [failMessage, setFailMessage] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successContact, setSuccessContact] = useState({
    phone: "",
    email: "",
  });

  useEffect(() => {
    const q = buildStaffClubEventQuery(managedClubId);
    if (!q) return;
    dispatch(currentEventListResponce(q));
  }, [managedClubId, dispatch, location.pathname]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const q = buildStaffClubEventQuery(managedClubId);
      if (!q) return;
      dispatch(currentEventListResponce(q));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [managedClubId, dispatch]);

  const event = useMemo(
    () => (events || []).find((e) => String(e._id) === String(eventId)),
    [events, eventId]
  );

  /** Paid tiers only — complimentary (₹0) guestlist tiers use Add Guestlist. */
  const tickets = useMemo(() => {
    const list = Array.isArray(event?.tickets) ? event.tickets : [];
    return list.filter((tk) => buyerDisplayPrice(tk) !== 0);
  }, [event?.tickets]);
  const ticket = tickets[ticketIndex] || null;

  useEffect(() => {
    const firstSellable = tickets.findIndex(
      (tk) => !normalizeTicketOfflineSoldOut(tk)
    );
    setTicketIndex(firstSellable >= 0 ? firstSellable : 0);
  }, [eventId, tickets]);

  const ticketPriceRaw = buyerDisplayPrice(ticket);
  const ticketPrice = Number(ticketPriceRaw);
  const phaseStatus = ticket ? resolvePhaseStatus(ticket) : null;
  const phaseCap = ticket ? buyerMaxQty(ticket, OFFLINE_SALE_QTY_MAX) : OFFLINE_SALE_QTY_MAX;
  const ticketTypeId = ticket?._id;
  const noofticket = Math.min(phaseCap || OFFLINE_SALE_QTY_MAX, clampOfflineQty(qty));
  const baseamount =
    Number.isFinite(ticketPrice) ? ticketPrice * noofticket : 0;
  const gst = 0;
  const amount = baseamount + gst;

  const filteredEvents = useMemo(() => {
    return (events || []).filter((ev) => {
      if (ev?.eventstatus === Constant.GENERALKEYS.CANCEL) return false;
      const cid = eventClubId(ev);
      if (cid == null || cid === "") return false;
      if (String(cid) !== String(managedClubId)) return false;
      if (isClubManager) return true;
      if (isClubEmployee) {
        return employeeMaySellOfflineEvent(ev, userId);
      }
      return true;
    });
  }, [events, managedClubId, isClubEmployee, isClubManager, userId]);

  const hasSellableTicketType = useMemo(() => {
    if (!ticket) return false;
    return !normalizeTicketOfflineSoldOut(ticket);
  }, [ticket]);

  const buildSalePayload = () => {
    if (!managedClubId) {
      dispatch(
        showToast({
          show: false,
          message: t("CLUB_WEBSITE_NO_CLUB"),
          type: "error",
        })
      );
      return null;
    }
    if (!eventId || !ticketTypeId) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_SELECT_EVENT_TICKET"),
          type: "error",
        })
      );
      return null;
    }
    const evClub = eventClubId(event);
    if (!evClub || String(evClub) !== String(managedClubId)) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_CLUB_MISMATCH"),
          type: "error",
        })
      );
      return null;
    }
    if (!Number.isFinite(ticketPrice)) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_INVALID_PRICE"),
          type: "error",
        })
      );
      return null;
    }
    if (normalizeTicketOfflineSoldOut(ticket)) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_TICKET_OFFLINE_SOLD_OUT"),
          type: "error",
        })
      );
      return null;
    }
    if (isPhasedPricingEnabled(ticket) && noofticket > phaseCap) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_PHASE_CROSS", { n: phaseCap }),
          type: "error",
        })
      );
      return null;
    }

    const nameTrim = (buyername ?? "").trim();
    const phoneTrim = (buyerphone ?? "").trim();
    const emailTrim = (buyeremail ?? "").trim();
    if (!nameTrim || !phoneTrim || !emailTrim) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_BUYER_REQUIRED"),
          type: "error",
        })
      );
      return null;
    }
    if (!Constant.REGEX.EMAIL.test(emailTrim)) {
      dispatch(
        showToast({
          show: false,
          message: t("OFFLINE_SALE_BUYER_EMAIL_INVALID"),
          type: "error",
        })
      );
      return null;
    }

    const body = {
      eventid: eventId,
      noofticket,
      ticketprice: ticketPrice,
      amount,
      currency: DEFAULT_CURRENCY,
      isdelivery: false,
      deliveryprice: 0,
      tickettypeid: ticketTypeId,
      gst,
      baseamount,
      baseprice: ticketPrice,
      offlinepaymentmethod: payment,
      inqueue: false,
      buyername: nameTrim,
      buyerphone: phoneTrim,
      buyeremail: emailTrim,
    };

    const ticketTitle =
      ticket?.tickettype || ticket?.name || `Type ${ticketIndex + 1}`;
    const payLabel =
      PAY_METHODS.find((p) => p.value === payment)?.label || payment;

    const summary = {
      itemLine: t("OFFLINE_SALE_CONFIRM_ITEM_LINE", {
        payment: payLabel,
        ticket: ticketTitle,
        price: ticketPrice,
        currency: DEFAULT_CURRENCY,
      }),
      quantity: noofticket,
      name: nameTrim,
      mobile: phoneTrim,
      email: emailTrim,
      paymentType: String(payment).toLowerCase(),
      amountDisplay: formatSaleAmount(amount, DEFAULT_CURRENCY),
    };

    return { body, summary };
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const built = buildSalePayload();
    if (!built) return;
    setPendingBody(built.body);
    setConfirmSummary(built.summary);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setPendingBody(null);
    setConfirmSummary(null);
  };

  const handleConfirmYes = () => {
    if (!pendingBody || busy) return;
    const body = pendingBody;
    setBusy(true);
    setConfirmOpen(false);
    setPendingBody(null);
    setConfirmSummary(null);
    postOfflineSale(body)
      .then((res) => {
        const msg = res?.message || res?.data?.message || t("OFFLINE_SALE_OK");
        dispatch(showToast({ show: false, message: msg, type: "success" }));
        setSuccessContact({
          phone: body.buyerphone ?? "",
          email: body.buyeremail ?? "",
        });
        setSuccessOpen(true);
        setQty(String(OFFLINE_SALE_QTY_MIN));
        setBuyername("");
        setBuyerphone("");
        setBuyeremail("");
      })
      .catch((err) => {
        const message =
          typeof err === "string"
            ? err
            : err?.message || t("OFFLINE_SALE_ERROR");
        setFailMessage(message);
        setFailOpen(true);
      })
      .finally(() => setBusy(false));
  };

  const closeSuccessModal = () => {
    setSuccessOpen(false);
  };

  const pageTitle = [{ title: t("OFFLINE_SALE_TITLE"), path: "#" }];

  return (
    <div className="pageContent categoryManagement club-staff-page-shell club-offline-sale-page">
      <Helmet>
        <title>{t("OFFLINE_SALE_TITLE")} | RizzTix</title>
      </Helmet>
      <Breadcrumbs
        innerPagetitle={t("OFFLINE_SALE_TITLE")}
        breadcrumbItem={t("OFFLINE_SALE_TITLE")}
        pageTitle={pageTitle}
        BreadcrumbInner={true}
        BreadcrumbmainTitle={true}
      />
      <Card className="border-0">
        <Card.Body>
          <p className="club-staff-help-strip small mb-3">
            {t("OFFLINE_SALE_HELP")}
          </p>
          {isClubEmployee ? (
            <div className="club-empref-strip small mb-3">
              {myEmprefLoading ? (
                <span className="text-muted">{t("LOADING")}</span>
              ) : myEmpref ? (
                <>
                  <span className="fw-semibold">
                    {t("CLUB_EMPLOYEE_MY_EMPREF", { empref: "" })}
                    <span className="club-empref-value">{myEmpref}</span>
                  </span>
                  <span className="text-muted d-block mt-1">
                    {t("CLUB_EMPLOYEE_MY_EMPREF_HINT")}
                  </span>
                </>
              ) : (
                <span className="text-muted">
                  {t("CLUB_EMPLOYEE_MY_EMPREF_UNAVAILABLE")}
                </span>
              )}
            </div>
          ) : null}
          {!managedClubId ? (
            <p className="text-danger">{t("CLUB_WEBSITE_NO_CLUB")}</p>
          ) : (
            <Form onSubmit={onSubmit} className="mt-2">
              <Form.Group className="mb-3">
                <Form.Label>{t("OFFLINE_SALE_EVENT")}</Form.Label>
                <Form.Select
                  value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  required
                >
                  <option value="">{t("OFFLINE_SALE_EVENT_PLACEHOLDER")}</option>
                  {filteredEvents.map((ev) => (
                    <option key={ev._id} value={ev._id}>
                      {ev.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {eventId && tickets.length === 0 ? (
                <p className="small text-muted mb-3">
                  {t("OFFLINE_SALE_NO_PAID_TIERS")}
                </p>
              ) : null}

              {tickets.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label>{t("OFFLINE_SALE_TICKET_TYPE")}</Form.Label>
                  <Form.Select
                    value={ticketIndex}
                    onChange={(e) =>
                      setTicketIndex(parseInt(e.target.value, 10) || 0)
                    }
                  >
                    {tickets.map((tk, idx) => {
                      const offlineBlocked = normalizeTicketOfflineSoldOut(tk);
                      const livePrice = buyerDisplayPrice(tk);
                      const st = resolvePhaseStatus(tk);
                      const phaseLabel = isPhasedPricingEnabled(tk)
                        ? ` · ${st.currentPhase?.name || "Phase"}`
                        : "";
                      return (
                        <option
                          key={tk._id || idx}
                          value={idx}
                          disabled={offlineBlocked || st.allPhasesSoldOut}
                        >
                          {tk.tickettype ||
                            tk.name ||
                            `Type ${idx + 1}`}{" "}
                          — {livePrice} {DEFAULT_CURRENCY}
                          {phaseLabel}
                          {offlineBlocked
                            ? ` (${t("TICKET_OFFLINE_SOLD_OUT")})`
                            : st.allPhasesSoldOut
                            ? ` (${t("OFFLINE_SALE_PHASE_SOLD_OUT")})`
                            : ""}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              )}

              <Row className="g-3 mb-1">
                <Col xs={12} sm={4} md={3}>
                  <Form.Group>
                    <Form.Label>{t("OFFLINE_SALE_QTY")}</Form.Label>
                    <Form.Control
                      type="number"
                      min={OFFLINE_SALE_QTY_MIN}
                      max={phaseCap || OFFLINE_SALE_QTY_MAX}
                      step={1}
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      onBlur={() =>
                        setQty(
                          String(
                            Math.min(phaseCap || OFFLINE_SALE_QTY_MAX, clampOfflineQty(qty))
                          )
                        )
                      }
                    />
                    {ticket && isPhasedPricingEnabled(ticket) ? (
                      <span className="pos-phase-hint">
                        {phaseStatus?.allPhasesSoldOut
                          ? t("OFFLINE_SALE_PHASE_SOLD_OUT")
                          : t("OFFLINE_SALE_PHASE_HINT", {
                              phase: phaseStatus?.currentPhase?.name || "Phase",
                              n: phaseStatus?.remainingInPhase ?? 0,
                            })}
                      </span>
                    ) : null}
                  </Form.Group>
                </Col>
                <Col xs={12} sm={8} md={9}>
                  <Form.Group>
                    <Form.Label>{t("OFFLINE_SALE_PAYMENT")}</Form.Label>
                    <Form.Select
                      value={payment}
                      onChange={(e) => setPayment(e.target.value)}
                    >
                      {PAY_METHODS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <p className="small text-muted fw-semibold mb-2 mt-4">
                {t("OFFLINE_SALE_BUYER_SECTION")}
              </p>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-1">
                  {t("OFFLINE_SALE_BUYER_NAME")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={buyername}
                  onChange={(e) => setBuyername(e.target.value)}
                  autoComplete="name"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-1">
                  {t("OFFLINE_SALE_BUYER_PHONE")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  value={buyerphone}
                  onChange={(e) => setBuyerphone(e.target.value)}
                  autoComplete="tel"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="small mb-1">
                  {t("OFFLINE_SALE_BUYER_EMAIL")}{" "}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={buyeremail}
                  onChange={(e) => setBuyeremail(e.target.value)}
                  autoComplete="email"
                  required
                />
                <Form.Text className="text-muted">
                  {t("OFFLINE_SALE_BUYER_REQUIRED_HINT")}
                </Form.Text>
              </Form.Group>

              <div className="club-offline-total mb-3 small text-muted">
                <span>{t("OFFLINE_SALE_TOTAL")}</span>
                <span>
                  <strong>{amount}</strong> {DEFAULT_CURRENCY}
                </span>
              </div>

              <div className="d-flex justify-content-center mt-2">
                <CustomButton
                  type="submit"
                  className="fillButton club-staff-btn-wide-mobile"
                  disabled={
                    busy ||
                    !eventId ||
                    !ticket ||
                    !hasSellableTicketType
                  }
                >
                  {busy ? t("LOADING") : t("OFFLINE_SALE_SUBMIT")}
                </CustomButton>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={confirmOpen}
        onHide={closeConfirm}
        centered
        backdrop="static"
        backdropClassName="club-offline-modal-backdrop"
        className="club-offline-sale-confirm-modal"
        contentClassName="border-0 rounded-3 shadow"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-uppercase fs-6 mb-0">
            {t("OFFLINE_SALE_CONFIRM_TITLE")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3 pb-2">
          {confirmSummary ? (
            <dl className="row mb-0 small club-offline-confirm-summary">
              <dt className="col-5 text-muted fw-normal">
                {t("OFFLINE_SALE_CONFIRM_ITEM")}
              </dt>
              <dd className="col-7">{confirmSummary.itemLine}</dd>
              <dt className="col-5 text-muted fw-normal">
                {t("OFFLINE_SALE_CONFIRM_QTY")}
              </dt>
              <dd className="col-7">{confirmSummary.quantity}</dd>
              <dt className="col-5 text-muted fw-normal">
                {t("OFFLINE_SALE_CONFIRM_NAME")}
              </dt>
              <dd className="col-7">{confirmSummary.name}</dd>
              <dt className="col-5 text-muted fw-normal">
                {t("OFFLINE_SALE_CONFIRM_MOBILE")}
              </dt>
              <dd className="col-7">{confirmSummary.mobile}</dd>
              <dt className="col-5 text-muted fw-normal">
                {t("OFFLINE_SALE_CONFIRM_EMAIL")}
              </dt>
              <dd className="col-7 text-break">{confirmSummary.email}</dd>
              <dt className="col-5 text-muted fw-normal">
                {t("OFFLINE_SALE_CONFIRM_PAYMENT_TYPE")}
              </dt>
              <dd className="col-7 text-capitalize">
                {confirmSummary.paymentType}
              </dd>
              <dt className="col-5 text-muted fw-normal">
                {t("OFFLINE_SALE_CONFIRM_AMOUNT")}
              </dt>
              <dd className="col-7 fw-semibold">{confirmSummary.amountDisplay}</dd>
            </dl>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0 justify-content-end gap-4">
          <button
            type="button"
            className="btn btn-link text-decoration-none fw-semibold text-dark p-0"
            onClick={closeConfirm}
          >
            {t("OFFLINE_SALE_CONFIRM_NO")}
          </button>
          <button
            type="button"
            className="btn btn-link text-decoration-none fw-semibold text-primary p-0"
            onClick={handleConfirmYes}
            disabled={busy}
          >
            {t("OFFLINE_SALE_CONFIRM_YES")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={failOpen}
        onHide={() => setFailOpen(false)}
        centered
        backdropClassName="club-offline-modal-backdrop"
        className="club-offline-sale-fail-modal"
        contentClassName="border-0 rounded-3 shadow"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold text-uppercase fs-6 text-danger mb-0">
            {t("OFFLINE_SALE_FAIL_TITLE")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <p className="mb-0 small">{failMessage}</p>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <button
            type="button"
            className="btn btn-primary btn-sm px-4"
            onClick={() => setFailOpen(false)}
          >
            {t("OFFLINE_SALE_FAIL_OK")}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={successOpen}
        onHide={closeSuccessModal}
        centered
        scrollable
        backdropClassName="club-offline-modal-backdrop"
        dialogClassName="club-offline-sale-success-dialog"
        className="club-offline-sale-result-modal club-offline-sale-result-success"
        contentClassName="d-flex flex-column border-0 shadow"
      >
        <Modal.Header
          closeButton
          className="flex-shrink-0 border-0 pb-0 club-offline-sale-success-header"
        />
        <Modal.Body className="px-3 px-sm-4 text-center pt-0 pb-4 club-offline-sale-success-body">
          <h2 className="fw-bold fs-4 mb-2">
            {t("OFFLINE_SALE_SUCCESS_TITLE")}
          </h2>
          <p className="fw-bold text-uppercase small mb-4">
            {t("OFFLINE_SALE_SUCCESS_SUBLINE")}
          </p>
          <div
            className="club-offline-success-check mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle"
            aria-hidden
          >
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M8 12.5l2.5 2.5L16 9"
                stroke="#fff"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p className="small text-muted mb-0">
            {t("OFFLINE_SALE_SUCCESS_SENT", {
              phone: successContact.phone,
              email: successContact.email,
            })}
          </p>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OfflineSaleContainer;
